import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  ButtonGroup,
  MenuItem,
  Select,
  FormControl,
  Alert,
  Chip,
} from "@mui/material";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import SearchBar from "../../components/common/SearchBar";
import useIsMobile from "../../components/hooks/useIsMobile";
import {
  useAdminListAllAdvisors,
  useListAllFamilies,
} from "../../services/admin";
import { useUpdateAdvisor, useUpdateFamily } from "../../services/advisor";
import {
  Advisor,
  AdvisorStatus,
  EntityMetadata,
  EntityMetadata_EntityMetadataField,
} from "../../protogen/common_pb";
import Loading from "../../components/common/Loading";
import {
  Family,
  FamilyStatus,
  UpdateAdvisorRequest,
  UpdateFamilyRequest,
} from "protogen/advisors_service_pb";
import {
  EditMetadataModal,
  FAMILY_ATTRIBUTION_FIELDS,
  FAMILY_DEMOGRAPHICS_FIELDS,
  fromProto,
  toProto,
} from "../../components/common/MetadataSection";
import LinkRouter from "components/navigation/LinkRouter";
import { useSearchParams } from "react-router-dom";
import { commaSeparatedEnglishList } from "../../common/utils";
import { Member } from "../../protogen/common_pb";
import DateDisplay from "components/common/DateDisplay";
import { useListSubmissions } from "../../services/signup";
import {
  ListSubmissionsRequest,
  ListSubmissionsRequest_ListSubmissionsFilter,
  SignupSubmission,
} from "protogen/signup_service_pb";

type FilterType = "all" | "needs_attribution" | "needs_demographic";
type EntityType = "advisors" | "members";
type ClickContext = "attribution" | "demographics" | "all";
type SortOrder = "asc" | "desc";

const getFamilyBadge = (family: Family) => {
  const values = new Map<FamilyStatus, [string, string, string]>([
    [FamilyStatus.ACTIVE, ["Active", "#ECF4EC", "#198282"]],
    [
      FamilyStatus.PROSPECT,
      ["Prospect", "rgba(251, 242, 229, 0.95)", "#9A6518"],
    ],
    [FamilyStatus.DEACTIVATED, ["Inactive", "#F5F5F5", "#6B6E7B"]],
    [
      FamilyStatus.DIGITAL_DEACTIVATED,
      ["Digital Inactive", "#F5F5F5", "#6B6E7B"],
    ],
    [
      FamilyStatus.DIGITAL_DEACTIVATED_TRIAL,
      ["Digital Trial Ended", "#F5F5F5", "#6B6E7B"],
    ],
    [FamilyStatus.TEST, ["Demo", "#FFF", "#6B6E7B"]],
    [FamilyStatus.DEMO, ["Demo", "#FFF", "#6B6E7B"]],
    [FamilyStatus.PREACTIVATION, ["Pre-Activation", "#FFF3CD", "#856404"]],
    [
      FamilyStatus.DEACTIVATED_PROSPECT,
      ["Inactive Prospect", "#F5F5F5", "#6B6E7B"],
    ],
  ]);
  if (!values.has(family.status)) {
    return null;
  }
  const [copy, bgColor, textColor] = values.get(family.status)!;
  return (
    <Chip
      label={copy}
      sx={{
        height: "24px",
        fontSize: "12px",
        fontWeight: "600",
        borderRadius: "100px",
        padding: "4px 8px",
        background: bgColor,
        span: { color: textColor },
        ...(bgColor === "#FFF" && { border: "1px solid #D4D4D4" }),
      }}
    />
  );
};

interface MetadataItemProps {
  type: EntityType;
  item: Advisor | Family;
  missingFields: string[];
  onClick: (context: ClickContext) => void;
  submission?: SignupSubmission;
}

const MetadataItem = ({
  type,
  item,
  missingFields,
  onClick,
  submission,
}: MetadataItemProps) => {
  const isMobile = useIsMobile();

  const getLink = () => {
    if (type === "advisors") {
      const advisor = item as Advisor;
      return `/tools/advisor-administration/${encodeURIComponent(advisor.ref)}`;
    } else {
      const family = item as Family;
      return `/families/${encodeURIComponent(family.ref)}`;
    }
  };

  const getName = () => {
    if (type === "advisors") {
      const advisor = item as Advisor;
      return `${advisor.firstName} ${advisor.lastName}`;
    } else {
      const family = item as Family;
      return family.name;
    }
  };

  const getFamilyMembers = () => {
    if (type === "advisors") {
      return "";
    } else {
      const family = item as Family;
      return commaSeparatedEnglishList(
        family.familyMembers.map((m: Member) => m.displayName),
      );
    }
  };

  const getExistingMetadataDisplay = () => {
    if (type === "advisors" || !item.metadata) {
      return null;
    }
    const family = item as Family;
    if (
      !family.metadata ||
      !family.metadata.fields ||
      family.metadata.fields.length === 0
    ) {
      return null;
    }

    const metadataItems = family.metadata.fields
      .filter(
        (field: EntityMetadata_EntityMetadataField) => field.key && field.value,
      )
      .slice(0, 3) // Show only first 3 items to avoid clutter
      .map(
        (field: EntityMetadata_EntityMetadataField) =>
          `${field.key}: ${field.value}`,
      );

    if (metadataItems.length === 0) {
      return null;
    }

    return (
      <Typography variant="bodySmall" color="#666" sx={{ marginRight: "8px" }}>
        {metadataItems.join(" • ")}
      </Typography>
    );
  };

  const getSignupAttributionDisplay = () => {
    const displayItems: string[] = [];

    if (submission) {
      if (submission.state?.referralSource) {
        displayItems.push(`Referral: ${submission.state.referralSource}`);
      }

      const couponId = submission.state?.payment?.couponId;
      const promoCode = submission.state?.payment?.promoCode;
      if (promoCode) {
        displayItems.push(`Promo: ${promoCode}`);
      } else if (couponId) {
        displayItems.push(`Coupon: ${couponId}`);
      }

      Object.entries(submission.trackingParameters || {}).forEach(
        ([key, value]) => {
          if (value) {
            displayItems.push(`${key}: ${value}`);
          }
        },
      );
    }

    return (
      <Typography variant="bodySmall" color="#666">
        {displayItems.join(" • ")}
      </Typography>
    );
  };

  const getEmail = () => {
    if (type === "advisors") {
      const advisor = item as Advisor;
      return advisor.personalEmail;
    } else {
      const family = item as Family;
      const primaryMember = family.familyMembers.find((m) => m.isPrimary);
      return primaryMember?.email || "";
    }
  };

  // Determine what buttons to show based on missing fields
  const getMissingCategories = () => {
    if (type === "advisors") {
      return {
        attribution: missingFields.includes("Channel"),
        demographics: false, // Advisors don't have demographics
      };
    } else {
      // For families
      return {
        attribution: missingFields.some((field) =>
          FAMILY_ATTRIBUTION_FIELDS.includes(field),
        ),
        demographics: missingFields.some((field) =>
          FAMILY_DEMOGRAPHICS_FIELDS.includes(field),
        ),
      };
    }
  };

  const { attribution, demographics } = getMissingCategories();

  return (
    <Box
      sx={{
        borderRadius: "8px",
        border: "1px solid #ECECEC",
        background: "#FFF",
        width: "100%",
        padding: "16px 24px",
        gap: "8px",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          borderColor: "#198282",
          boxShadow: "0 2px 8px rgba(25, 130, 130, 0.1)",
        },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "flex-start" : "center"}
        gap="12px"
      >
        <Box flexGrow={1}>
          <Box display="flex" flexDirection="column" gap="4px">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Box display="flex" flexDirection="column" gap="4px">
                <Box display="flex" alignItems="center" gap="8px">
                  <LinkRouter to={getLink()}>
                    <Typography variant="bodyHeavy" color="#198282">
                      {getName()}
                    </Typography>
                  </LinkRouter>
                  {type === "members" && getFamilyBadge(item as Family)}
                </Box>
                <Typography variant="body" color="#666">
                  {type === "advisors" ? getEmail() : getFamilyMembers()}
                </Typography>
                {type === "advisors" && (
                  <Typography variant="body" color="#666">
                    {getEmail()}
                  </Typography>
                )}
              </Box>
              <Box display="flex" alignSelf="flex-start">
                <DateDisplay
                  date={new Date(Number(item.createdAtSec) * 1000)}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box display="flex" alignItems="center" gap="8px">
        {getSignupAttributionDisplay()}
      </Box>
      <Box display="flex" alignItems="center" gap="8px">
        {getExistingMetadataDisplay()}
        <Button
          variant="text"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onClick("all");
          }}
        >
          Edit metadata
        </Button>
      </Box>
      {missingFields.length > 0 && (
        <Alert severity="warning" sx={{ marginTop: "8px" }}>
          <Box display="flex" flexDirection="column">
            <Typography variant="body" color="#666">
              Missing: {missingFields.join(", ")}
            </Typography>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="flex-end"
              alignItems="center"
              gap="8px"
            >
              {attribution && (
                <Button
                  variant="text"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick("attribution");
                  }}
                >
                  Add attribution
                </Button>
              )}
              {demographics && attribution && " • "}
              {demographics && (
                <Button
                  variant="text"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick("demographics");
                  }}
                >
                  Add demographics
                </Button>
              )}
            </Box>
          </Box>
        </Alert>
      )}
    </Box>
  );
};

const hasMetadataField = (metadata: any, fieldName: string): boolean => {
  if (!metadata || !metadata.fields) return false;
  return metadata.fields.some((field: any) => field.key === fieldName);
};

const getAdvisorMissingFields = (advisor: Advisor): string[] => {
  const missing: string[] = [];

  if (!hasMetadataField(advisor.metadata, "channel")) {
    missing.push("Channel");
  }

  return missing;
};

const getFamilyMissingFields = (family: Family): string[] => {
  const missing: string[] = [];

  // Check attribution fields
  if (!hasMetadataField(family.metadata, "referrer")) {
    missing.push("Referrer");
  }
  if (!hasMetadataField(family.metadata, "channel")) {
    missing.push("Channel");
  }

  // Check demographic fields
  if (!hasMetadataField(family.metadata, "Is parent")) {
    missing.push("Is Parent");
  }
  if (!hasMetadataField(family.metadata, "Number of children")) {
    missing.push("Number of Children");
  }
  if (!hasMetadataField(family.metadata, "Home value")) {
    missing.push("Home Value");
  }
  if (!hasMetadataField(family.metadata, "Relationship status")) {
    missing.push("Relationship status");
  }

  return missing;
};

const filterByType = (
  items: (Advisor | Family)[],
  entityType: EntityType,
  filterType: FilterType,
) => {
  return items.filter((item) => {
    const missingFields =
      entityType === "advisors"
        ? getAdvisorMissingFields(item as Advisor)
        : getFamilyMissingFields(item as Family);

    switch (filterType) {
      case "all":
        return true;
      case "needs_attribution":
        if (entityType === "advisors") {
          return missingFields.includes("Channel");
        } else {
          return missingFields.some((field) =>
            ["Referrer", "Channel"].includes(field),
          );
        }
      case "needs_demographic":
        if (entityType === "advisors") {
          return false; // Disabled for advisors
        } else {
          return missingFields.some((field) =>
            [
              "Is Parent",
              "Number of Children",
              "Home Value",
              "Relationship Status",
            ].includes(field),
          );
        }
      default:
        return true;
    }
  });
};

const stringMatch = (
  item: Advisor | Family,
  query: string,
  entityType: EntityType,
) => {
  const searchText = query.toLowerCase();

  if (entityType === "advisors") {
    const advisor = item as Advisor;
    return (
      advisor.firstName.toLowerCase().includes(searchText) ||
      advisor.lastName.toLowerCase().includes(searchText) ||
      advisor.personalEmail.toLowerCase().includes(searchText)
    );
  } else {
    const family = item as Family;
    const primaryMember = family.familyMembers.find((m) => m.isPrimary);
    return (
      family.name.toLowerCase().includes(searchText) ||
      (primaryMember?.email || "").toLowerCase().includes(searchText)
    );
  }
};

export default () => {
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();

  // Helper to validate URL params
  const getValidEntityType = (param: string | null): EntityType => {
    if (param === "advisors" || param === "members") return param;
    return "members";
  };

  const getValidFilterType = (param: string | null): FilterType => {
    if (
      param === "all" ||
      param === "needs_attribution" ||
      param === "needs_demographic"
    )
      return param;
    return "all";
  };

  // Initialize state from URL params or defaults
  const [entityType, setEntityType] = useState<EntityType>(
    getValidEntityType(searchParams.get("type")),
  );
  const [filterType, setFilterType] = useState<FilterType>(
    getValidFilterType(searchParams.get("filter")),
  );
  const [query, setQuery] = useState<string>(searchParams.get("search") || "");
  const [selectedItem, setSelectedItem] = useState<Advisor | Family | null>(
    null,
  );
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [clickContext, setClickContext] = useState<ClickContext>("all");
  const [familyStatusFilter, setFamilyStatusFilter] = useState<
    FamilyStatus | "all"
  >("all");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

  const {
    request: requestAdvisors,
    data: advisorsData,
    loading: advisorsLoading,
  } = useAdminListAllAdvisors();
  const {
    request: requestFamilies,
    data: familiesData,
    loading: familiesLoading,
  } = useListAllFamilies();
  const {
    request: requestSubmissions,
    data: submissionsData,
    loading: submissionsLoading,
  } = useListSubmissions();
  const { request: updateAdvisor } = useUpdateAdvisor();
  const { request: updateFamily } = useUpdateFamily();

  // Helper function to update both state and URL params
  const updateUrlParams = (
    updates: Partial<{
      type: EntityType;
      filter: FilterType;
      search: string;
    }>,
  ) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value && value !== "") {
            newParams.set(key, value);
          } else {
            newParams.delete(key);
          }
        });
        return newParams;
      },
      { replace: true },
    );
  };

  const handleEntityTypeChange = (newType: EntityType) => {
    setEntityType(newType);
    updateUrlParams({ type: newType });

    // Reset family-specific filters when switching to advisors
    if (newType === "advisors") {
      setFamilyStatusFilter("all");
    }
  };

  const handleFilterTypeChange = (newFilter: FilterType) => {
    setFilterType(newFilter);
    updateUrlParams({ filter: newFilter });
  };

  const handleQueryChange = (newQuery: string) => {
    setQuery(newQuery);
    updateUrlParams({ search: newQuery });
  };

  const refresh = async () => {
    if (entityType === "advisors") {
      await requestAdvisors({ advisorRefs: [] });
    } else {
      await requestFamilies();
      // Fetch all signup submissions to get tracking parameters
      await requestSubmissions(
        new ListSubmissionsRequest({
          filter: ListSubmissionsRequest_ListSubmissionsFilter.ALL,
        }),
      );
    }
  };

  useEffect(() => {
    refresh();
  }, [entityType]);

  const handleItemClick = (item: Advisor | Family, context: ClickContext) => {
    setSelectedItem(item);
    setClickContext(context);
    setEditDialogOpen(true);
  };

  const getContextualMissingKeys = (
    item: Advisor | Family,
    context: ClickContext,
  ): string[] => {
    if (context === "all") {
      return []; // No pre-population for "View metadata"
    }

    const allMissingFields =
      entityType === "advisors"
        ? getAdvisorMissingFields(item as Advisor)
        : getFamilyMissingFields(item as Family);

    if (context === "attribution") {
      const attributionFields = ["Referrer", "Channel"];
      return allMissingFields
        .filter((field) => attributionFields.includes(field))
        .map((field) => {
          // Map display names to actual metadata keys
          const fieldMap: { [key: string]: string } = {
            Referrer: "referrer",
            Channel: "channel",
          };
          return fieldMap[field] || field.toLowerCase();
        });
    }

    if (context === "demographics") {
      const demographicsFields = [
        "Is Parent",
        "Number of Children",
        "Home Value",
        "Relationship Status",
      ];
      return allMissingFields
        .filter((field) => demographicsFields.includes(field))
        .map((field) => {
          // Map display names to actual metadata keys
          const fieldMap: { [key: string]: string } = {
            "Is Parent": "Is parent",
            "Number of Children": "Number of children",
            "Home Value": "Home value",
            "Relationship Status": "Relationship status",
          };
          return fieldMap[field] || field;
        });
    }

    return [];
  };

  const handleSaveMetadata = async (metadata: { [key: string]: string }) => {
    if (!selectedItem) return;

    const protoMetadata = toProto(metadata);

    if (entityType === "advisors") {
      const advisor = selectedItem as Advisor;
      await updateAdvisor(
        new UpdateAdvisorRequest({
          advisorRef: advisor.ref,
          shouldUpdateMetadata: true,
          updatedMetadata: protoMetadata as EntityMetadata,
          shouldUpdateSpecialties: false,
        }),
      );
    } else {
      const family = selectedItem as Family;
      await updateFamily(
        new UpdateFamilyRequest({
          familyRef: family.ref,
          shouldUpdateMetadata: true,
          updatedMetadata: protoMetadata as EntityMetadata,
        }),
      );
    }

    refresh();
    setEditDialogOpen(false);
  };
  const SKIP_ADVISOR_STATUS = [AdvisorStatus.TEST, AdvisorStatus.STAFF];
  const SKIP_FAMILY_STATUS = [
    FamilyStatus.TEST,
    FamilyStatus.DEMO,
    FamilyStatus.TEST_HIDDEN,
    FamilyStatus.UNPAID_SIGNUP,
  ];
  const currentData =
    entityType === "advisors"
      ? advisorsData?.advisors.filter(
          (a) => !SKIP_ADVISOR_STATUS.includes(a.status),
        ) || []
      : familiesData?.families.filter(
          (f) => !SKIP_FAMILY_STATUS.includes(f.status),
        ) || [];

  const loading =
    entityType === "advisors"
      ? advisorsLoading
      : familiesLoading || submissionsLoading;

  // Create a map of family ref to signup submission
  const submissionsByFamilyRef = new Map<string, SignupSubmission>();
  if (submissionsData?.submissions) {
    submissionsData.submissions.forEach((submission) => {
      if (submission.family?.ref) {
        submissionsByFamilyRef.set(submission.family.ref, submission);
      }
    });
  }

  let filteredItems = filterByType(currentData, entityType, filterType);

  if (query) {
    filteredItems = filteredItems.filter((item) =>
      stringMatch(item, query, entityType),
    );
  }

  // Apply family status filter (only for members)
  if (entityType === "members" && familyStatusFilter !== "all") {
    filteredItems = filteredItems.filter((item) => {
      const family = item as Family;
      return family.status === familyStatusFilter;
    });
  }

  // Apply sorting
  filteredItems = filteredItems.sort((a, b) => {
    return sortOrder === "asc"
      ? Number(b.createdAtSec - a.createdAtSec)
      : Number(a.createdAtSec - b.createdAtSec);
  });

  const itemsWithMissingFields = filteredItems
    .map((item) => {
      const missingFields =
        entityType === "advisors"
          ? getAdvisorMissingFields(item as Advisor)
          : getFamilyMissingFields(item as Family);
      return { item, missingFields };
    })
    .filter(({ missingFields }) => {
      // Only show items that actually have missing fields for the selected filter
      if (filterType === "all") return missingFields.length > 0;
      return missingFields.length > 0;
    });

  return (
    <Box
      sx={{
        margin: isMobile ? "" : "auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px 20px 0px 20px" : "64px 64px 0 64px",
      }}
    >
      <Box display="flex" flexDirection="row">
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          marginBottom="16px"
        >
          <Breadcrumbs
            breadcrumbs={[
              { name: "Home", link: "/" },
              { name: "Tools", link: "/tools" },
            ]}
          />
          <Box
            display="flex"
            width="100%"
            gap={isMobile ? "12px" : "24px"}
            {...(isMobile && { flexDirection: "column" })}
            {...(!isMobile && {
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            })}
          >
            <Typography variant="display">Pipeline Metadata</Typography>
            {!isMobile && (
              <SearchBar
                initialQuery={query}
                fullWidth={false}
                autofocus={false}
                onQuery={handleQueryChange}
                onKeyPress={true}
                sx={{ minWidth: "300px" }}
              />
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: isMobile ? "flex-start" : "flex-end",
          gap: isMobile ? "12px" : "24px",
          marginBottom: "16px",
        }}
      >
        {isMobile && (
          <SearchBar
            initialQuery={query}
            fullWidth={true}
            autofocus={true}
            onQuery={handleQueryChange}
            onKeyPress={true}
            sx={{ marginLeft: "0" }}
          />
        )}

        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          gap="12px"
          alignItems={isMobile ? "stretch" : "center"}
          justifyContent="space-between"
          width="100%"
        >
          <ButtonGroup
            style={{
              display: "flex",
              ...(isMobile && {
                width: "100%",
              }),
            }}
          >
            <Button
              sx={{ flex: "1", width: "100%" }}
              variant={
                entityType === "members" ? "buttonGroupSelected" : "buttonGroup"
              }
              onClick={() => handleEntityTypeChange("members")}
            >
              Members
            </Button>
            <Button
              sx={{ flex: "1", width: "100%" }}
              variant={
                entityType === "advisors"
                  ? "buttonGroupSelected"
                  : "buttonGroup"
              }
              onClick={() => handleEntityTypeChange("advisors")}
            >
              Advisors
            </Button>
          </ButtonGroup>

          <Box
            display="flex"
            flexDirection="row"
            gap="12px"
            alignItems="center"
            flexWrap="wrap"
          >
            {/* Family Status Filter - only show for members */}
            {entityType === "members" && (
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={familyStatusFilter}
                  onChange={(e) =>
                    setFamilyStatusFilter(
                      e.target.value as FamilyStatus | "all",
                    )
                  }
                  variant="outlined"
                  label="Status"
                  sx={{
                    minHeight: "42.25px",
                    ".MuiSelect-select": {
                      marginTop: "0",
                    },
                  }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value={FamilyStatus.ACTIVE}>Active</MenuItem>
                  <MenuItem value={FamilyStatus.PROSPECT}>Prospect</MenuItem>
                  <MenuItem value={FamilyStatus.PREACTIVATION}>
                    Pre-Activation
                  </MenuItem>
                  <MenuItem value={FamilyStatus.DEACTIVATED}>Inactive</MenuItem>
                  <MenuItem value={FamilyStatus.DIGITAL_DEACTIVATED}>
                    Digital Inactive
                  </MenuItem>
                  <MenuItem value={FamilyStatus.DIGITAL_DEACTIVATED_TRIAL}>
                    Digital Trial Ended
                  </MenuItem>
                  <MenuItem value={FamilyStatus.DEACTIVATED_PROSPECT}>
                    Inactive Prospect
                  </MenuItem>
                </Select>
              </FormControl>
            )}

            {/* Sort Order */}
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as SortOrder)}
                variant="outlined"
                label="Order"
                sx={{
                  minHeight: "42.25px",
                  ".MuiSelect-select": {
                    marginTop: "0",
                  },
                }}
              >
                <MenuItem value="asc">Newest</MenuItem>
                <MenuItem value="desc">Oldest</MenuItem>
              </Select>
            </FormControl>

            {/* Missing Fields Filter */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={filterType}
                onChange={(e) =>
                  handleFilterTypeChange(e.target.value as FilterType)
                }
                variant="outlined"
                label="Missing"
                sx={{
                  minHeight: "42.25px",
                  ".MuiSelect-select": {
                    marginTop: "0",
                  },
                }}
              >
                <MenuItem value="all">Needs something</MenuItem>
                <MenuItem value="needs_attribution">Needs Attribution</MenuItem>
                <MenuItem
                  value="needs_demographic"
                  disabled={entityType === "advisors"}
                >
                  Needs Demographic
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: "16px",
        }}
      >
        {loading && <Loading />}

        {!itemsWithMissingFields.length && !loading && (
          <Box>
            <Alert severity="info">No items found with missing metadata</Alert>
          </Box>
        )}

        {!loading && itemsWithMissingFields.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {itemsWithMissingFields.map(({ item, missingFields }, index) => {
              const submission =
                entityType === "members"
                  ? submissionsByFamilyRef.get(item.ref)
                  : undefined;
              return (
                <MetadataItem
                  key={`${entityType}-${item.ref || index}`}
                  type={entityType}
                  item={item}
                  missingFields={missingFields}
                  onClick={(context) => handleItemClick(item, context)}
                  submission={submission}
                />
              );
            })}
          </Box>
        )}
      </Box>

      {/* Metadata Editing Modal */}
      {selectedItem && editDialogOpen && (
        <EditMetadataModal
          metadata={fromProto(selectedItem.metadata || { fields: [] })}
          handleSave={handleSaveMetadata}
          open={editDialogOpen}
          handleClose={() => setEditDialogOpen(false)}
          entityType={entityType === "advisors" ? "advisor" : "family"}
          missingKeys={getContextualMissingKeys(selectedItem, clickContext)}
        />
      )}
    </Box>
  );
};
